import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useQuery } from '@tanstack/react-query'
import { CreditDecisionHistory, IncomesHistory } from '../../types/IncomesHistory'

// Query Keys
export const creditApplicationHistoryKeys = {
  all: ['creditApplicationHistory'] as const,
  incomesHistory: (creditApplicationId: string, financingProgramId: string) =>
    [...creditApplicationHistoryKeys.all, 'incomesHistory', creditApplicationId, financingProgramId] as const,
  creditDecisionHistory: (creditApplicationId: string, financingProgramId: string) =>
    [...creditApplicationHistoryKeys.all, 'creditDecisionHistory', creditApplicationId, financingProgramId] as const,
}

const BASE_URL = '/Applications/{financingProgramId}/{creditApplicationId}/Revisions'

// API Functions
export async function getIncomesHistory(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): Promise<IncomesHistory> {
  const apiClient = getApiClient()
  const url = BASE_URL.replace('{financingProgramId}', financingProgramId)
    .replace('{creditApplicationId}', creditApplicationId)
    .concat('/IncomesRevision')
  const response = await apiClient.get<IncomesHistory>(url)
  return response.data
}

export async function getCreditDecisionHistory(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): Promise<CreditDecisionHistory[]> {
  const apiClient = getApiClient()
  const url = BASE_URL.replace('{financingProgramId}', financingProgramId)
    .replace('{creditApplicationId}', creditApplicationId)
    .concat('/DecisionsRevision')
  const response = await apiClient.get<CreditDecisionHistory[]>(url)
  return response.data
}

// React-Query Hooks
export function useGetIncomesHistory(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
  enabled = true,
): [IncomesHistory | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditApplicationHistoryKeys.incomesHistory(creditApplicationId, financingProgramId),
    queryFn: () => getIncomesHistory(creditApplicationId, financingProgramId),
    enabled: enabled && !!creditApplicationId,
  })
  return [data ?? null, isFetching]
}

export function useGetCreditDecisionHistory(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
  enabled = true,
): [CreditDecisionHistory[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditApplicationHistoryKeys.creditDecisionHistory(creditApplicationId, financingProgramId),
    queryFn: () => getCreditDecisionHistory(creditApplicationId, financingProgramId),
    enabled: enabled && !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}
